<template>
  <div class="route-page">
    <div class="route-card">
      <el-form :inline="true" :model="query" class="route-query" @submit.native.prevent>
        <el-form-item label="路线名称">
          <el-input v-model.trim="query.routeName" clearable placeholder="请输入路线名称" @keyup.enter.native="search" />
        </el-form-item>
        <el-form-item label="起点">
          <el-input v-model.trim="query.startAddress" clearable placeholder="请输入起点关键字" @keyup.enter.native="search" />
        </el-form-item>
        <el-form-item label="起点地区">
          <el-cascader v-model="query.startAreaCodes" :options="regionOptions" :props="areaFilterProps" clearable placeholder="请选择起点地区" />
        </el-form-item>
        <el-form-item label="终点">
          <el-input v-model.trim="query.endAddress" clearable placeholder="请输入终点关键字" @keyup.enter.native="search" />
        </el-form-item>
        <el-form-item label="终点地区">
          <el-cascader v-model="query.endAreaCodes" :options="regionOptions" :props="areaFilterProps" clearable placeholder="请选择终点地区" />
        </el-form-item>
        <el-form-item label="路线状态">
          <el-select v-model="query.status" clearable placeholder="全部" style="width: 120px" @change="search">
            <el-option label="全部" value="" />
            <el-option label="正常" :value="1" />
            <el-option label="禁用" :value="0" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="search">查询</el-button>
          <el-button icon="el-icon-refresh-left" @click="reset">重置</el-button>
        </el-form-item>
      </el-form>

      <div class="route-toolbar">
        <el-button v-permission="'route:add'" type="primary" icon="el-icon-plus" @click="createRoute">新建运输路线</el-button>
        <el-tooltip content="刷新" placement="top"><el-button icon="el-icon-refresh" @click="fetchList" /></el-tooltip>
      </div>

      <el-table v-loading="loading" :data="pageList" border stripe>
        <el-table-column prop="routeName" label="路线名称" min-width="160" align="center" show-overflow-tooltip />
        <el-table-column label="起点" min-width="210" align="center" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ locationText(row, 'start') }}</template>
        </el-table-column>
        <el-table-column label="途经点" width="100" align="center">
          <template slot-scope="{ row }">{{ waypointCount(row) }} 个</template>
        </el-table-column>
        <el-table-column label="终点" min-width="210" align="center" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ locationText(row, 'end') }}</template>
        </el-table-column>
        <el-table-column label="规划里程" width="110" align="center">
          <template slot-scope="{ row }">{{ mileageText(row.totalDileage) }}</template>
        </el-table-column>
        <el-table-column label="预计时长" width="120" align="center">
          <template slot-scope="{ row }">{{ durationText(row.totalDuration) }}</template>
        </el-table-column>
        <el-table-column label="路线状态" width="100" align="center">
          <template slot-scope="{ row }"><el-tag :type="Number(row.status) === 1 ? 'success' : 'info'" size="small">{{ Number(row.status) === 1 ? '正常' : '禁用' }}</el-tag></template>
        </el-table-column>
        <el-table-column label="创建时间" width="170" align="center">
          <template slot-scope="{ row }">{{ formatDate(row.createTime) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="365" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click="viewRoute(row)">查看地图</el-button>
            <el-button v-permission="'route:edit'" type="text" size="small" @click="editRoute(row)">编辑</el-button>
            <el-button v-if="Number(row.status) === 1" v-permission="'route:edit'" type="text" size="small" @click="importCoordinates(row)">导入坐标</el-button>
            <el-button v-permission="'route:edit'" type="text" size="small" @click="changeStatus(row)">{{ Number(row.status) === 1 ? '禁用' : '启用' }}</el-button>
            <el-button v-permission="'route:delete'" type="text" size="small" class="route-delete" @click="removeRoute(row)">删除</el-button>
            <el-button v-permission="'route:delete'" type="text" size="small" @click="showReferences(row)">关联运单</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="route-pagination">
        <el-pagination background :current-page="page" :page-size="limit" :page-sizes="[10, 20, 50]" :total="list.length" layout="total, sizes, prev, pager, next, jumper" @size-change="changePageSize" @current-change="changePage" />
      </div>
    </div>
    <RouteCoordinatesDialog :visible.sync="coordinatesVisible" :route="coordinatesRoute" @saved="fetchList" />
    <el-dialog title="关联运单" :visible.sync="referencesVisible" width="680px" @close="closeReferences">
      <el-table v-loading="referencesLoading" :data="references" border>
        <el-table-column prop="waybillNo" label="运单号" min-width="200" />
        <el-table-column label="运输状态" width="140"><template slot-scope="{ row }">{{ referenceStatusText(row.transportStatus) }}</template></el-table-column>
        <el-table-column label="创建时间" width="180"><template slot-scope="{ row }">{{ formatDate(row.createTime) }}</template></el-table-column>
      </el-table>
      <el-pagination background layout="total, prev, pager, next" :current-page="referencesPage" :page-size="referencesLimit" :total="referencesTotal" @current-change="changeReferencesPage" />
      <span slot="footer"><el-button @click="referencesVisible = false">关闭</el-button></span>
    </el-dialog>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import RouteCoordinatesDialog from './RouteCoordinatesDialog.vue'
import { codeToText, regionData } from 'element-china-area-data'
import { deleteRoute, getRouteList, getRouteReferences, isRequestSuccess, updateRouteStatus } from './lingdongApi'
import { defaultBusinessOptions, businessOptionLabel } from '@/utils/businessDictionaries'

const initialQuery = () => ({ routeName: '', startAddress: '', startAreaCodes: [], endAddress: '', endAreaCodes: [], status: 1 })

export default {
  name: 'RouteManage',
  components: { RouteCoordinatesDialog },
  data() {
    return {
      loading: false,
      coordinatesVisible: false,
      coordinatesRoute: null,
      requestId: 0,
      referencesVisible: false,
      referencesLoading: false,
      referencesRequestId: 0,
      references: [],
      referencesTotal: 0,
      referencesPage: 1,
      referencesLimit: 10,
      referenceRouteId: null,
      list: [],
      query: initialQuery(),
      page: 1,
      limit: 10,
      regionOptions: regionData,
      areaFilterProps: { checkStrictly: true }
    }
  },
  computed: {
    pageList() {
      const start = (this.page - 1) * this.limit
      return this.list.slice(start, start + this.limit)
    }
  },
  created() { this.fetchList() },
  activated() { this.fetchList() },
  beforeDestroy() { this.requestId += 1; this.referencesRequestId += 1 },
  methods: {
    async fetchList() {
      const requestId = ++this.requestId
      this.loading = true
      try {
        const params = { ...this.query }
        if (params.status === '') delete params.status
        delete params.startAreaCodes
        delete params.endAreaCodes
        const startAreaName = this.areaName(this.query.startAreaCodes)
        const endAreaName = this.areaName(this.query.endAreaCodes)
        if (startAreaName) params.startAreaName = startAreaName
        if (endAreaName) params.endAreaName = endAreaName
        const res = await getRouteList(params)
        if (requestId !== this.requestId) return
        if (!isRequestSuccess(res)) throw new Error(res.msg || '查询失败')
        this.list = res.data || []
        const maxPage = Math.max(1, Math.ceil(this.list.length / this.limit))
        if (this.page > maxPage) this.page = maxPage
      } catch (e) {
        if (requestId !== this.requestId) return
        this.list = []
        this.$message.error(e.msg || e.message || '查询失败')
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
    search() { this.page = 1; this.fetchList() },
    reset() { this.query = initialQuery(); this.page = 1; this.fetchList() },
    changePageSize(size) { this.limit = size; this.page = 1 },
    changePage(page) { this.page = page },
    importCoordinates(row) { this.coordinatesRoute = { ...row }; this.coordinatesVisible = true },
    createRoute() { this.$router.push({ name: 'LingDongRouteCreateName' }) },
    editRoute(row) { this.$router.push({ name: 'LingDongRouteEditName', params: { id: row.id } }) },
    viewRoute(row) { this.$router.push({ name: 'LingDongRouteViewName', params: { id: row.id } }) },
    async changeStatus(row) {
      const status = Number(row.status) === 1 ? 0 : 1
      const action = status === 1 ? '启用' : '禁用'
      try {
        await this.$confirm(`确认${action}路线“${row.routeName}”吗？`, '提示', { type: 'warning' })
        const res = await updateRouteStatus({ id: row.id, status })
        if (!isRequestSuccess(res)) throw new Error(res.msg || `${action}失败`)
        this.$message.success(`${action}成功`)
        this.fetchList()
      } catch (e) {
        if (e && e !== 'cancel' && e !== 'close') this.$message.error(e.msg || e.message || `${action}失败`)
      }
    },
    async removeRoute(row) {
      try {
        await this.$confirm(`确认删除路线“${row.routeName}”吗？删除后不可恢复。`, '提示', { type: 'warning' })
        const res = await deleteRoute(row.id)
        if (!isRequestSuccess(res)) throw new Error(res.msg || '删除失败')
        this.$message.success('路线已删除')
        this.fetchList()
      } catch (e) {
        if (e && e !== 'cancel' && e !== 'close') {
          const message = e.msg || e.message || '删除失败'
          this.$message.error(message)
          if (/关联|引用|运单/.test(message)) this.showReferences(row)
        }
      }
    },
    referenceStatusText(status) { return businessOptionLabel('waybill_status', defaultBusinessOptions('waybill_status'), status) },
    showReferences(row) {
      this.referenceRouteId = row.id
      this.referencesPage = 1
      this.references = []
      this.referencesTotal = 0
      this.referencesVisible = true
      return this.fetchReferences()
    },
    closeReferences() { this.referencesRequestId += 1; this.referencesLoading = false },
    changeReferencesPage(page) { this.referencesPage = page; this.fetchReferences() },
    async fetchReferences() {
      const requestId = ++this.referencesRequestId
      this.referencesLoading = true
      try {
        const res = await getRouteReferences(this.referenceRouteId, { page: this.referencesPage, limit: this.referencesLimit })
        if (requestId !== this.referencesRequestId) return
        if (!isRequestSuccess(res)) throw new Error(res.msg || '查询关联运单失败')
        this.references = (res.data && res.data.items) || []
        this.referencesTotal = Number(res.data && res.data.total) || 0
      } catch (error) {
        if (requestId !== this.referencesRequestId) return
        this.references = []
        this.referencesTotal = 0
        this.$message.error(error.msg || error.message || '查询关联运单失败')
      } finally {
        if (requestId === this.referencesRequestId) this.referencesLoading = false
      }
    },
    waypointCount(row) {
      try { return Array.isArray(JSON.parse(row.waypoints || '[]')) ? JSON.parse(row.waypoints || '[]').length : 0 } catch (e) { return 0 }
    },
    areaName(codes) { return Array.isArray(codes) ? codes.map(code => codeToText[code] || '').filter(Boolean).join('') : '' },
    locationText(row, type) {
      const areaName = row[`${type}AreaName`] || ''
      const address = row[`${type}Address`] || ''
      return areaName && !address.startsWith(areaName) ? `${areaName}${address}` : address || areaName || '-'
    },
    mileageText(value) { return value === null || value === undefined ? '-' : `${Number(value).toFixed(2)} 公里` },
    durationText(value) {
      if (!value) return '-'
      const seconds = Number(value)
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.ceil((seconds % 3600) / 60)
      return hours ? `${hours}小时${minutes}分` : `${minutes}分`
    },
    formatDate(value) { return value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-' }
  }
}
</script>

<style scoped>
.route-page { width: 100%; min-height: 100%; padding: 16px; box-sizing: border-box; background: #f0f2f5; }
.route-card { min-height: 100%; padding: 16px; border-radius: 4px; background: #fff; box-sizing: border-box; }
.route-query { margin-bottom: 4px; }
.route-toolbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.route-pagination { display: flex; justify-content: flex-end; margin-top: 12px; }
.route-delete { color: #e64545; }
</style>
